import React, { useEffect, useState, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { CSVLink } from "react-csv";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import { BrandsTypes, toLakhs } from "../../../utils/constants";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import SheetFiveLoader from "./SheetFiveLoader";
import useCategories from "../useCategories";

const SheetFivePreview = () => {
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 5);
    start.setDate(1);
    end.setDate(1);
    return [start, end];
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [data, setData] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBrandType, setSelectedBrandType] = useState(2);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMonthBrand, setSelectedMonthBrand] = useState({ month: "", brandId: null, amount: 0 });
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandSalesPersons, setBrandSalesPersons] = useState([]);
  const [totalSalesPersonWise, setTotalSalesPersonWise] = useState(0);
  const [showSales, setShowSales] = useState(false);
  const [searchBrand, setSearchBrand] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const {CategorySelect, selectedCategories} = useCategories()

  const formatDateRangeForAPI = (range) => {
    if (!range?.[0] || !range?.[1]) return null;
    const startStr = `${range[0].getFullYear()}-${String(range[0].getMonth() + 1).padStart(2, "0")}`;
    const endStr = `${range[1].getFullYear()}-${String(range[1].getMonth() + 1).padStart(2, "0")}`;
    return [startStr, endStr];
  };

  const fetchData = async () => {
    if (!dateRange[0] || !dateRange[1]) return;
    setIsLoading(true);
    setError(null);
    const apiDateRange = formatDateRangeForAPI(dateRange);
    if (!apiDateRange) return;

    try {
      const response = await API({
        url: "/phionex_reports/getSheet5Data",
        method: "POST",
        data: { 
          dateRange: apiDateRange, 
          sales: showSales,
          brand_type: selectedBrandType, 
          sales_person: selectedSalesPerson,
          selectedCategories,
          search: debouncedSearch.trim() || undefined
        },
      });

      if (response.status === "SUCCESS") {
        const brandTypeMap = {};
        
        response.data.forEach(item => {
          const { brand_id, brand_name, category_name, new_user_type, sales_person_id, sales_person = "Unknown", month, total_amount, insertions } = item;
          
          if (!brandTypeMap[new_user_type]) {
            brandTypeMap[new_user_type] = {
              brandType: new_user_type,
              brands: {}
            };
          }

          if (!brandTypeMap[new_user_type].brands[brand_id]) {
            brandTypeMap[new_user_type].brands[brand_id] = {
              brand_id,
              brand_name,
              category_name,
              salesPersons: {}
            };
          }

          if (!brandTypeMap[new_user_type].brands[brand_id].salesPersons[sales_person]) {
            brandTypeMap[new_user_type].brands[brand_id].salesPersons[sales_person] = {
              sales_person,
              sales_person_id,
              months: {},
              totalAmount: 0,
              totalInsertions: 0
            };
          }

          const amount = parseFloat(total_amount);
          const insertionCount = parseInt(insertions || 0);
          brandTypeMap[new_user_type].brands[brand_id].salesPersons[sales_person].months[month] = { amount, insertions: insertionCount };
          brandTypeMap[new_user_type].brands[brand_id].salesPersons[sales_person].totalAmount += amount;
          brandTypeMap[new_user_type].brands[brand_id].salesPersons[sales_person].totalInsertions += insertionCount;
        });

        const aggregatedData = [];
        Object.values(brandTypeMap).forEach(brandTypeGroup => {
          Object.values(brandTypeGroup.brands).forEach(brand => {
            if (showSales) {
              Object.values(brand.salesPersons).forEach(salesPersonEntry => {
                aggregatedData.push({
                  ...brandTypeGroup,
                  ...brand,
                  ...salesPersonEntry,
                  months: Object.entries(salesPersonEntry.months).map(([month, total_amount]) => ({
                    month,
                    total_amount
                  }))
                });
              });
            } else {
              const aggregatedSalesPerson = {
                sales_person: "Total",
                months: {},
                totalAmount: 0,
                totalInsertions: 0
              };

              Object.values(brand.salesPersons).forEach(salesPersonEntry => {
                Object.entries(salesPersonEntry.months).forEach(([month, data]) => {
                  if (!aggregatedSalesPerson.months[month]) {
                    aggregatedSalesPerson.months[month] = { amount: 0, insertions: 0 };
                  }
                  aggregatedSalesPerson.months[month].amount += data.amount;
                  aggregatedSalesPerson.months[month].insertions += data.insertions;
                });
                aggregatedSalesPerson.totalAmount += salesPersonEntry.totalAmount;
                aggregatedSalesPerson.totalInsertions += salesPersonEntry.totalInsertions;
              });

              aggregatedData.push({
                ...brandTypeGroup,
                ...brand,
                ...aggregatedSalesPerson,
                months: Object.entries(aggregatedSalesPerson.months).map(([month, data]) => ({
                  month,
                  total_amount: data.amount,
                  insertions: data.insertions
                }))
              });
            }
          });
        });

        setData(aggregatedData);
      } else {
        setError(response.message || "Failed to fetch data");
      }
    } catch (err) {
      setError("An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedBrandType, showSales, selectedSalesPerson, selectedCategories, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchBrand);
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchBrand]);

  useEffect(() => {
    // if (!selectedBrandType) {
    //   setSalesPersons([]);
    //   setSelectedSalesPerson([]);
    //   return;
    // }
    
    const getSalesPersons = async () => {
      try {
        const result = await API({
          url: "/phionex_reports/getSalesPersons",
          method: "POST",
          data: { selectedBrandType },
        });
        if (result.status === "SUCCESS") {
          setSalesPersons(result.data);
        } else {
          Toast("error", result.message);
        }
      } catch (err) {
        Toast("error", "Failed to fetch sales persons");
      }
    };
    getSalesPersons();
  }, []);

  const fetchLineItems = async (brandId, month, sales_person) => {
    const apiDateRange = formatDateRangeForAPI([new Date(`${month}-01`), new Date(`${month}-01`)]);
    try {
      const result = await API({
        url: "/phionex_reports/getLineItemsByBrand",
        method: "POST",
        data: { 
          dateRange: apiDateRange, 
          brand_id: brandId,
          brand_type: selectedBrandType, 
          sales_person: sales_person ? [sales_person] : selectedSalesPerson 
        },
      });
      
      if (result.status === "SUCCESS") {
        const totalBudget = result.data?.reduce((sum, item) => 
          sum + parseFloat(item.total_spent || 0), 0
        );
        setLineItems({ data: result.data, totalBudget });
      } else {
        setLineItems([]);
      }
    } catch (err) {
      setLineItems([]);
    }
  };

  const fetchSalesPersons = async (brand_id) => {
    const apiDateRange = formatDateRangeForAPI(dateRange);
    try {
      const result = await API({
        url: "/phionex_reports/getSalesPersonByBrand",
        method: "POST",
        data: { 
          dateRange: apiDateRange, 
          brand_id, 
          brand_type: selectedBrandType, 
          sales_person: selectedSalesPerson 
        },
      });
      if (result.status === "SUCCESS") {
        setBrandSalesPersons(result.data);
        let sum = 0;
        result?.data?.forEach(item => sum += +item.spent);
        setTotalSalesPersonWise(sum);
      } else {
        setBrandSalesPersons([]);
      }
    } catch (err) {
      setBrandSalesPersons([]);
    }
  };

  const handleMonthClick = (brandId, month, amount, sales_person) => {
    setSelectedMonthBrand({ month, brandId, amount });
    fetchLineItems(brandId, month, sales_person);
    setSelectedBrand(null);
    setBrandSalesPersons([]);
    setDrawerOpen(true);
  };

  const handleBrandClick = (brandId, brandName) => {
    setSelectedBrand({ brandId, brandName });
    fetchSalesPersons(brandId);
    setSelectedMonthBrand({ month: "", brandId: null, amount: 0 });
    setLineItems([]);
    setDrawerOpen(true);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${monthNames[+month - 1]}-${year.slice(2)}`;
  };

  const uniqueMonths = useMemo(() => 
    [...new Set(data.flatMap(item => item.months.map(m => m.month)))].sort(), [data]
  );

  const monthTotals = useMemo(() => {
    const totals = {};
    uniqueMonths.forEach(month => {
      totals[month] = { amount: 0, insertions: 0 };
      data.forEach(item => {
        const monthData = item.months.find(m => m.month === month);
        if (monthData) {
          totals[month].amount += parseFloat(monthData.total_amount);
          totals[month].insertions += parseInt(monthData.insertions);
        }
      });
    });
    return totals;
  }, [data, uniqueMonths]);

  const grandTotal = useMemo(() => 
    data.reduce((sum, item) => ({ amount: sum.amount + item.totalAmount, insertions: sum.insertions + item.totalInsertions }), { amount: 0, insertions: 0 }), [data]
  );

  const groupedByBrandType = useMemo(() => {
    const groups = {};
    data.forEach(item => {
      const brandType = item.brandType;
      if (!groups[brandType]) {
        groups[brandType] = [];
      }
      groups[brandType].push(item);
    });
    return groups;
  }, [data]);

  const csvData = useMemo(() => {
    const headers = ["Brand Type", "Brand", "Category", ...(showSales ? ["Sales Person"] : []), ...uniqueMonths.flatMap(m => [`${formatMonth(m)} Amount`, `${formatMonth(m)} Insertions`]), "Total Amount", "Total Insertions"];
    
    const rows = data.map(item => {
      const row = [BrandsTypes.find(brand => brand.value === +item.brandType)?.label || "Unknown", item.brand_name, item.category_name];
      if (showSales && item.sales_person && item.sales_person !== "Total") {
        row.push(item.sales_person);
      }
      uniqueMonths.forEach(month => {
        const monthData = item.months.find(m => m.month === month);
        row.push(monthData ? monthData.total_amount : "0.00");
        row.push(monthData ? monthData.insertions : "0");
      });
      row.push(item.totalAmount);
      row.push(item.totalInsertions);
      return row;
    });

    const totalRow = ["TOTAL", "", "", ...(showSales ? [""] : []), ...uniqueMonths.flatMap(m => [monthTotals[m].amount, monthTotals[m].insertions]), grandTotal.amount, grandTotal.insertions];
    rows.push(totalRow);

    return [headers, ...rows];
  }, [data, uniqueMonths, monthTotals, grandTotal, showSales]);

  const lineItemsTotal = useMemo(() => 
    lineItems.data?.reduce((sum, item) => sum + parseFloat(item.total_spent || 0), 0) || 0, [lineItems]
  );

  if (isLoading) {
    return <SheetFiveLoader rows={15} olumns={showSales ? 8 : 7  } className="p-4 max-w-full" />;
  }

  return (
    <div className="p-4 max-w-full">
      <div className="flex flex-wrap gap-6 items-end rounded px-4 py-2 bg-white mb-6">
        <div className="flex flex-col">
          <label className="mb-1 font-semibold">Select Date Range *</label>
          <DatePicker
            selected={dateRange[0]}
            onChange={setDateRange}
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            selectsRange
            dateFormat="MM/yyyy"
            showMonthYearPicker
            className="border rounded px-3 py-2 w-48"
            maxDate={new Date()}
          />
        </div>
        <div className="flex flex-col min-w-[180px]">
          <label className="mb-1 font-semibold">Brand Type</label>
          <Select
            options={BrandsTypes}
            value={BrandsTypes.find(b => +b.value === +selectedBrandType) || null}
            onChange={item => setSelectedBrandType(item?.value || null)}
            placeholder="Select Brand Type"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className={`flex flex-col min-w-[220px] ${selectedBrandType ? "" : "opacity-50 pointer-events-none"}`}>
          <label className="mb-1 font-semibold">Sales Person(s)</label>
          <Select
            options={salesPersons}
            value={salesPersons.filter(p => selectedSalesPerson.includes(+p.value))}
            onChange={items => setSelectedSalesPerson(items ? items.map(i => i.value) : [])}
            isMulti
            isClearable
            placeholder="Select Sales Person(s)"
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            closeMenuOnSelect={false}
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-2 font-semibold">Show Sales Persons</label>
          <button 
            className={`w-16 h-6 rounded-full flex items-center border-2 ${ showSales ? 'justify-end' : 'justify-start'}`} 
            onClick={() => setShowSales(!showSales)}
          >
            <div className={`w-4 h-4 rounded-full m-1 ${ showSales ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
          </button>
        </div>
        <div className="flex flex-col min-w-[200px]">
          <label className="mb-1 font-semibold">Search Brand</label>
          <input
            type="text"
            value={searchBrand}
            onChange={(e) => setSearchBrand(e.target.value)}
            placeholder="Enter brand name..."
            className="border rounded px-3 py-2 w-56"
          />
        </div>
        <CategorySelect />
        <CSVLink 
          data={csvData} 
          filename={`sheet_five_data_${showSales ? 'with_sales_persons' : 'aggregated'}.csv`} 
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded transition-colors"
        >
          Download CSV
        </CSVLink>
      </div>

      {error ? (
        <div className="text-red-500 text-center p-4">{error}</div>
      ) : Object.keys(groupedByBrandType).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedByBrandType).map(([brandType, brandTypeData]) => (
            <div className="flex gap-6">
              <div className="bg-blue-100 p-1 w-32 font-bold shadow rounded-md h-8 text-center flex flex-col">
                <spn>{BrandsTypes.find(item=>item.value===+brandType)?.label} </spn>
              </div>
              <div key={brandType} className="w-full flex items-center justify-center bg-white shadow-lg rounded-lg overflow-hidden">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-yellow-200 text-gray-800">
                        <th className="border p-2 font-semibold">Brand</th>
                        <th className="border p-2 font-semibold">Category</th>
                        {showSales && <th className="border p-2 font-semibold">Sales Person</th>}
                        {uniqueMonths.flatMap(month => [
                          <th key={`${month}-amount`} className="border p-2 font-semibold text-center">
                            {formatMonth(month)} Amount
                          </th>,
                          <th key={`${month}-insertions`} className="border p-2 font-semibold text-center">
                            {formatMonth(month)} Insertions
                          </th>
                        ])}
                        <th className="border p-2 font-semibold text-center">Total Amount</th>
                        <th className="border p-2 font-semibold text-center">Total Insertions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandTypeData.map(item => (
                        <tr key={`${item.brand_id}-${item.sales_person}`} className="hover:bg-gray-50">
                          <td 
                            className={`border p-2 ${ showSales ? " text-black" : "cursor-pointer text-blue-600 hover:underline"}`}
                            onClick={!showSales ? () => handleBrandClick(item.brand_id, item.brand_name) : undefined}
                          >
                            {item.brand_name}
                          </td>
                          <td className="border p-2">{item.category_name}</td>
                          {showSales && (
                            <td className="border p-2">
                              {item.sales_person === "Total" ? <strong>Total</strong> : item.sales_person}
                            </td>
                          )}
                          {uniqueMonths.flatMap(month => {
                            const monthData = item.months.find(m => m.month === month);
                            return [
                              <td
                                key={`${month}-amount`}
                                className="border p-2 text-right cursor-pointer text-blue-600 hover:underline"
                                onClick={() => monthData && handleMonthClick(item.brand_id, monthData.month, monthData.total_amount, item.sales_person_id || null)}
                                title={`₹${monthData?.total_amount || 0}`}
                              >
                                {monthData ? toLakhs(monthData.total_amount) : "-"}
                              </td>,
                              <td
                                key={`${month}-insertions`}
                                className="border p-2 text-right"
                                title={monthData?.insertions || 0}
                              >
                                {monthData ? monthData.insertions : "-"}
                              </td>
                            ];
                          })}
                          <td 
                            className="border p-2 text-right font-semibold bg-gray-50" 
                            title={`₹${item.totalAmount}`}
                          >
                            {toLakhs(item.totalAmount)}
                          </td>
                          <td 
                            className="border p-2 text-right font-semibold bg-gray-50" 
                            title={item.totalInsertions}
                          >
                            {item.totalInsertions}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            </div>
          ))}
          
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-yellow-200 text-gray-800">
                    <th className="border p-2 font-semibold">Brand</th>
                    <th className="border p-2 font-semibold">Category</th>
                    {showSales && <th className="border p-2 font-semibold">Sales Person</th>}
                    {uniqueMonths.flatMap(month => [
                      <th key={`${month}-amount`} className="border p-2 font-semibold text-center">
                        {formatMonth(month)} Amount
                      </th>,
                      <th key={`${month}-insertions`} className="border p-2 font-semibold text-center">
                        {formatMonth(month)} Insertions
                      </th>
                    ])}
                    <th className="border p-2 font-semibold text-center">Total Amount</th>
                    <th className="border p-2 font-semibold text-center">Total Insertions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan={showSales ? 3 : 2} className="border p-2 text-right">GRAND TOTAL</td>
                    {uniqueMonths.flatMap(month => [
                      <td key={`${month}-amount`} className="border p-2 text-right bg-yellow-50" title={`₹${monthTotals[month].amount}`}>
                        {toLakhs(monthTotals[month].amount)}
                      </td>,
                      <td key={`${month}-insertions`} className="border p-2 text-right bg-yellow-50" title={monthTotals[month].insertions}>
                        {monthTotals[month].insertions}
                      </td>
                    ])}
                    <td className="border p-2 text-right bg-yellow-100" title={`₹${grandTotal.amount}`}>
                      {toLakhs(grandTotal.amount)}
                    </td>
                    <td className="border p-2 text-right bg-yellow-100" title={grandTotal.insertions}>
                      {grandTotal.insertions}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center p-4 text-gray-500">No data available</div>
      )}

      <SlDrawer
        open={drawerOpen}
        onSlAfterHide={() => setDrawerOpen(false)}
        style={{ "--size": `${window.innerWidth <= 767 ? "100%" : "40%"}` }}
      >
        <div className="p-4 max-h-full overflow-auto">
          {selectedMonthBrand.brandId ? (
            <>
              <h3 className="text-lg font-bold mb-4">
                Line Items: {formatMonth(selectedMonthBrand.month)} - {toLakhs(selectedMonthBrand.amount)}
              </h3>
              {lineItems.data?.length > 0 ? (
                <div>
                  <table className="w-full border-collapse bg-white shadow-lg rounded-lg mb-4">
                    <thead>
                      <tr className="bg-yellow-200">
                        <th className="border p-2">Campaign ID</th>
                        <th className="border p-2">Campaign Name</th>
                        <th className="border p-2 text-right">Total Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.data?.map((item, index) => (
                        <tr key={item.campaign_id || index} className="hover:bg-gray-50">
                          <td className="border p-2">{item.campaign_id}</td>
                          <td className="border p-2">{item.campaign_name}</td>
                          <td className="border p-2 text-right" title={item.total_spent}>{toLakhs(item.total_spent)}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 font-bold">
                        <td colSpan="2" className="border p-2 text-right">Total</td>
                        <td className="border p-2 text-right bg-yellow-50" title={`₹${lineItemsTotal}`}>
                          {toLakhs(lineItemsTotal)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center p-4 text-gray-500">No line items available</div>
              )}
            </>
          ) : selectedBrand ? (
            <>
              <h3 className="text-lg font-bold mb-4">
                Sales Persons for {selectedBrand.brandName}
              </h3>
              {brandSalesPersons.length > 0 ? (
                <div>
                  <table className="w-full border-collapse bg-white shadow-lg rounded-lg mb-4">
                    <thead>
                      <tr className="bg-yellow-200">
                        <th className="border p-2">Sales Person ID</th>
                        <th className="border p-2">Name</th>
                        <th className="border p-2 text-right">Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandSalesPersons.map((sp, index) => (
                        <tr key={sp.sales_person_id || index} className="hover:bg-gray-50">
                          <td className="border p-2">{sp.sales_person_id}</td>
                          <td className="border p-2">{sp.name}</td>
                          <td className="border p-2 text-right" title={`₹${sp.spent}`}>
                            {toLakhs(sp.spent)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 font-bold">
                        <td colSpan="2" className="border p-2 text-right">Total</td>
                        <td className="border p-2 text-right bg-yellow-50" title={`₹${totalSalesPersonWise}`}>
                          {toLakhs(totalSalesPersonWise)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center p-4 text-gray-500">No sales persons available</div>
              )}
            </>
          ) : null}
        </div>
      </SlDrawer>
    </div>
  );
};

export default SheetFivePreview;